import { Box, styled, Typography } from "@material-ui/core";
import { EXAM_COLORS } from "../../_lib/epocus-exam-colors";
import { EXAM_ICONS, EXAM_LABELS } from "../../_lib/epocus-exam-constants";
import { UltrasoundScanner } from "../../_lib/epocus-icons";
import {
  EPOCUS_BG,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { getFirstName, PerformanceHeader } from "../../_lib/performance-header";
import {
  getScoreColor,
  SCORE_GOOD,
  SCORE_OK,
} from "../../_lib/qa-score-colors";
import { ScoreSparkline } from "../../_lib/score-sparkline";
import {
  BigValue,
  Card,
  MetaLabel,
  SectionTitle,
  SummaryGrid,
} from "../../_lib/stat-primitives";
import type { ExamTypeReviewCount, ScoreBucket } from "./use-reviewer-stats";
import { useReviewerStats } from "./use-reviewer-stats";

const EMPTY_RATE = "—";

const formatRate = (rate: number | null): string =>
  rate === null ? EMPTY_RATE : `${rate}%`;

// ─── Summary Cards ────────────────────────────────────────────────────────────

const ScansReviewedCard: FC<{ count: number; trend: number[] }> = ({
  count,
  trend,
}) => (
  <Card>
    <MetaLabel>Scans Reviewed</MetaLabel>
    <Box
      display="flex"
      alignItems="baseline"
      style={{ gap: 8, marginBottom: 8 }}
    >
      <BigValue>{count}</BigValue>
      <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
        scans
      </Typography>
    </Box>
    <ScoreSparkline data={trend} color="#3B82F6" />
  </Card>
);

const AverageScoreCard: FC<{ score: string; trend: number[] }> = ({
  score,
  trend,
}) => (
  <Card>
    <MetaLabel>Average Score Given</MetaLabel>
    <Box
      display="flex"
      alignItems="baseline"
      style={{ gap: 8, marginBottom: 8 }}
    >
      <BigValue style={{ color: getScoreColor(score) }}>{score}</BigValue>
      {score !== "—" && (
        <Typography style={{ fontSize: rem(14), color: EPOCUS_TEXT_SECONDARY }}>
          / 5
        </Typography>
      )}
    </Box>
    <ScoreSparkline data={trend} color={SCORE_GOOD} />
  </Card>
);

const AgreementCard: FC<{
  agreementRate: number | null;
  commentRate: number | null;
}> = ({ agreementRate, commentRate }) => (
  <Card>
    <MetaLabel>Agreement Rate</MetaLabel>
    <Box
      display="flex"
      alignItems="baseline"
      flexWrap="wrap"
      style={{ gap: 8, marginBottom: 8 }}
    >
      <BigValue>{formatRate(agreementRate)}</BigValue>
      {agreementRate !== null && (
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
          agreed with findings
        </Typography>
      )}
    </Box>
    <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
      {commentRate === null
        ? "No reviews yet"
        : `${formatRate(commentRate)} of reviews include a written comment`}
    </Typography>
  </Card>
);

// ─── Score Distribution ───────────────────────────────────────────────────────

const DistributionRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 6,
});

const BarTrack = styled(Box)({
  position: "relative",
  flex: 1,
  height: 8,
  borderRadius: 4,
  backgroundColor: EPOCUS_BG,
  overflow: "hidden",
});

const ScoreDistributionSection: FC<{ distribution: ScoreBucket[] }> = ({
  distribution,
}) => {
  const max = Math.max(...distribution.map((b) => b.count));

  return (
    <Box>
      <SectionTitle>Scores I Gave</SectionTitle>
      <Card>
        {max === 0 ? (
          <Typography
            style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
          >
            No reviews yet
          </Typography>
        ) : (
          distribution.map(({ score, count }) => (
            <DistributionRow key={score}>
              <Typography
                style={{
                  fontSize: rem(12),
                  fontWeight: 600,
                  color: EPOCUS_TEXT_SECONDARY,
                  width: 26,
                }}
              >
                {score.toFixed(1)}
              </Typography>
              <BarTrack>
                <Box
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: `${(count / max) * 100}%`,
                    borderRadius: 4,
                    backgroundColor: getScoreColor(score.toFixed(1)),
                    transition: "width 0.4s ease",
                  }}
                />
              </BarTrack>
              <Typography
                style={{
                  fontSize: rem(12),
                  fontWeight: 700,
                  color: EPOCUS_TEXT_PRIMARY,
                  width: 20,
                  textAlign: "right",
                }}
              >
                {count}
              </Typography>
            </DistributionRow>
          ))
        )}
      </Card>
    </Box>
  );
};

// ─── Reviews by Exam Type ─────────────────────────────────────────────────────

const ExamTypeGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
});

const ExamTypeCountCard: FC<{ stats: ExamTypeReviewCount }> = ({ stats }) => {
  const { examType, count } = stats;
  const ExamIcon = EXAM_ICONS[examType] ?? UltrasoundScanner;

  return (
    <Card style={{ padding: "14px 14px 12px" }}>
      <Box display="flex" alignItems="center" style={{ gap: 6 }}>
        <ExamIcon
          width={18}
          height={18}
          fill={EPOCUS_ICON_ACTIVE}
          aria-hidden
        />
        <Typography
          style={{
            fontSize: rem(12),
            fontWeight: 600,
            color: EPOCUS_TEXT_PRIMARY,
          }}
        >
          {EXAM_LABELS[examType]}
        </Typography>
      </Box>
      <Typography
        style={{
          fontSize: rem(22),
          fontWeight: 700,
          color: EXAM_COLORS[examType],
          marginTop: 6,
        }}
      >
        {count}
      </Typography>
    </Card>
  );
};

const ByExamTypeSection: FC<{ byExamType: ExamTypeReviewCount[] }> = ({
  byExamType,
}) => (
  <Box>
    <SectionTitle>Reviews by Exam Type</SectionTitle>
    {byExamType.length === 0 ? (
      <Card>
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
          No reviews yet
        </Typography>
      </Card>
    ) : (
      <ExamTypeGrid>
        {byExamType.map((stats) => (
          <ExamTypeCountCard key={stats.examType} stats={stats} />
        ))}
      </ExamTypeGrid>
    )}
  </Box>
);

// ─── Feedback Tags ────────────────────────────────────────────────────────────

const FeedbackTagsSection: FC<{
  tags: Array<{ tag: string; pct: number }>;
}> = ({ tags }) => (
  <Box>
    <SectionTitle>Feedback I Give Most</SectionTitle>
    <Card>
      {tags.length === 0 ? (
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
          No reviews yet
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
          {tags.map(({ tag, pct }) => (
            <Box
              key={tag}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                style={{ fontSize: rem(13), color: EPOCUS_TEXT_PRIMARY }}
              >
                {tag}
              </Typography>
              <Typography
                style={{
                  fontSize: rem(13),
                  fontWeight: 700,
                  color: pct >= 50 ? SCORE_GOOD : SCORE_OK,
                }}
              >
                {pct}%
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Card>
  </Box>
);

// ─── Variant ──────────────────────────────────────────────────────────────────

export const ReviewerPerformance: FC<{
  reviewerId: string;
  name: string | undefined;
}> = ({ reviewerId, name }) => {
  const {
    scansReviewed,
    averageScoreGiven,
    agreementRate,
    commentRate,
    scoreTrend,
    scoreDistribution,
    volumeTrend,
    topFeedbackTags,
    byExamType,
  } = useReviewerStats(reviewerId);

  return (
    <>
      <PerformanceHeader
        title="My Performance"
        subtitle={`${getFirstName(name)} · reviewing activity`}
      />

      <SummaryGrid>
        <ScansReviewedCard count={scansReviewed} trend={volumeTrend} />
        <AverageScoreCard score={averageScoreGiven} trend={scoreTrend} />
        <AgreementCard
          agreementRate={agreementRate}
          commentRate={commentRate}
        />
      </SummaryGrid>

      <ScoreDistributionSection distribution={scoreDistribution} />

      <ByExamTypeSection byExamType={byExamType} />

      <FeedbackTagsSection tags={topFeedbackTags} />
    </>
  );
};
